import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  CssBaseline,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Badge,
  Breadcrumbs,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  NavigateNext as NavigateNextIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import Sidebar from "./Sidebar";
import NotificationPanel from "../notifications/NotificationPanel";
import { useAuth } from "../../contexts/AuthContext";
import { THEME_CONSTANTS } from "../../theme/constants";
import { useChatStore } from "../../stores/chatStore";
import { PageInfoProvider, usePageInfo } from "../../contexts/PageInfoContext";
import type { BaseComponentProps } from "../../types";

const DRAWER_WIDTH = 260;

interface MainLayoutProps extends BaseComponentProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = (props) => {
  return (
    <PageInfoProvider>
      <MainLayoutInner {...props} />
    </PageInfoProvider>
  );
};

const MainLayoutInner: React.FC<MainLayoutProps> = ({
  children,
  className,
  "data-testid": dataTestId,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { windowState } = useChatStore();
  const { pageInfo } = usePageInfo();

  // Derive page title from breadcrumbs (last item) or fallback
  const pageTitle =
    pageInfo.breadcrumbs.length > 0
      ? pageInfo.breadcrumbs[pageInfo.breadcrumbs.length - 1].label
      : "Alpha Health";

  // Calculate chat window width for responsive layout
  // Only apply width adjustment on desktop (md+), on mobile/tablet chat is an overlay
  const chatWidth =
    !isMobile && windowState.isOpen && !windowState.isMinimized
      ? windowState.width
      : 0;

  // Close sidebar when switching to mobile
  React.useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
  };

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  return (
    <Box
      className={className}
      data-testid={dataTestId}
      {...props}
      sx={{ display: "flex", minHeight: "100vh" }}
    >
      <CssBaseline />

      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          right: {
            xs: 0,
            md: chatWidth > 0 ? `${chatWidth}px` : 0,
          },
          left: {
            xs: 0,
            md: sidebarOpen ? `${DRAWER_WIDTH}px` : 0,
          },
          width: "auto",
          transition: theme.transitions.create(["left", "right"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          backgroundColor: "background.paper",
          color: "text.primary",
          boxShadow: THEME_CONSTANTS.shadows.base,
          borderBottom: `1px solid ${theme.palette.divider}`,
          // Enhanced mobile-first design with consistent height
          zIndex: theme.zIndex.drawer + 1,
          height: { xs: 56, sm: 64 },
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: THEME_CONSTANTS.layout.headerHeight },
            px: { xs: 2, sm: 3 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
              flex: 1,
            }}
          >
            <IconButton
              color="inherit"
              aria-label="Open navigation menu"
              edge="start"
              onClick={handleSidebarToggle}
              sx={{
                // Enhanced touch target for mobile
                minWidth: { xs: 44, sm: "auto" },
                minHeight: { xs: 44, sm: "auto" },
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="h6"
                  noWrap
                  component="h1"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    fontSize: { xs: "1rem", sm: "1.125rem" },
                    lineHeight: 1.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {pageTitle}
                </Typography>
                {pageInfo.subtitle && (
                  <Tooltip title={pageInfo.subtitle} arrow placement="right">
                    <InfoIcon
                      fontSize="small"
                      sx={{
                        color: "text.disabled",
                        flexShrink: 0,
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              {pageInfo.breadcrumbs.length > 0 && (
                <Breadcrumbs
                  separator={
                    <NavigateNextIcon
                      sx={{ fontSize: 14, color: "text.disabled" }}
                    />
                  }
                  sx={{
                    "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" },
                    "& .MuiBreadcrumbs-separator": { mx: 0.5 },
                  }}
                >
                  {pageInfo.breadcrumbs.map((item) => (
                    <Typography
                      key={`appbar-bc-${item.label}`}
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ fontSize: "0.75rem" }}
                    >
                      {item.label}
                    </Typography>
                  ))}
                </Breadcrumbs>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1 },
            }}
          >
            <IconButton
              color="inherit"
              aria-label="View notifications"
              onClick={handleNotificationOpen}
              sx={{
                color: "text.secondary",
                // Enhanced touch targets
                minWidth: { xs: 44, sm: "auto" },
                minHeight: { xs: 44, sm: "auto" },
              }}
            >
              <Badge color="error" variant="dot" invisible={false}>
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <IconButton
              color="inherit"
              aria-label="Account menu"
              onClick={handleUserMenuOpen}
              sx={{
                color: "text.secondary",
                minWidth: { xs: 44, sm: "auto" },
                minHeight: { xs: 44, sm: "auto" },
              }}
            >
              {user ? (
                <Avatar
                  sx={{
                    width: { xs: 36, sm: 32 },
                    height: { xs: 36, sm: 32 },
                    fontSize: "14px",
                    bgcolor: "primary.main",
                  }}
                >
                  {user.firstName?.[0]}
                  {user.lastName?.[0]}
                </Avatar>
              ) : (
                <AccountIcon />
              )}
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Notification Panel */}
      <NotificationPanel
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationClose}
      />

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleUserMenuClose}
        onClick={handleUserMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {user && [
          <MenuItem key="user-info" disabled>
            <Avatar sx={{ bgcolor: "primary.main" }}>
              {user.firstName?.[0]}
              {user.lastName?.[0]}
            </Avatar>
            <Box>
              <Typography variant="subtitle2">{user.displayName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
          </MenuItem>,
          <Divider key="divider" />,
        ]}
        <MenuItem
          onClick={() => {
            handleUserMenuClose();
            navigate("/account/security");
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        variant={isMobile ? "temporary" : "persistent"}
      />

      {/* Main Content */}
      <Box
        component="main"
        role="main"
        aria-label="Main content area"
        sx={{
          flexGrow: 1,
          flexShrink: 1,
          minWidth: 0,
          // Remove margin-based layout, let flexbox handle it
          // Use margin-right to reserve space for chat window
          marginRight: `${chatWidth}px`,
          transition: theme.transitions.create(["margin-right"], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          backgroundColor: "background.default",
          minHeight: "100vh",
          // Reduced top padding to prevent title cutoff
          pt: {
            xs: "56px",
            sm: "64px",
            md: "64px",
            lg: "64px",
          },
          px: {
            xs: 2, // 16px padding on mobile
            sm: 3, // 24px padding on tablet and up
          },
          pb: 3, // 24px bottom padding
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;
